/**
 * API service for Campaigns
 * A campaign is a sequenced series of identical work orders — a pharma batch
 * series ("APAP 10mg batches 1-9"), a vehicle fleet order ("50 Rentokil
 * Mavericks"), or a repeating food SKU run. Generating from a campaign
 * creates numbered work orders (campaignSeq); repeat calls extend the
 * sequence rather than restarting it.
 */
import axios from "../utils/api-client";

const API_BASE_URL = `${process.env.REACT_APP_API_END_POINT}/Campaigns`;

export interface Campaign {
	id: number;
	companyId: number;
	name: string;
	productId?: number | null;
	productName?: string | null;
	laborStandardId?: number | null;
	targetCount: number;
	notes?: string | null;
	isActive: boolean;
	/** How many sequenced work orders have been generated so far */
	generatedCount: number;
	createdDate: string;
}

export interface SaveCampaignDto {
	companyId: number;
	name: string;
	productId?: number;
	laborStandardId?: number;
	targetCount: number;
	notes?: string;
}

export interface CampaignWorkOrder {
	id: number;
	sku: string;
	startTime: string;
	endTime: string;
	workstation: string;
	campaignSeq: number;
	orderType: string;
	quantity: number;
}

export interface GenerateCampaignOrdersDto {
	count: number;
	startDate: string;
	workstation: string;
	/** Days between consecutive orders; decimals allowed (0.5 = two per day) */
	spacingDays: number;
	quantity?: number;
	/**
	 * JSON string keyed by attrKey (see work-order-attributes-api).
	 * The `{seq}` token is substituted with the order's campaignSeq.
	 */
	attributeValuesTemplate?: string;
	/** The plant the batches are for; each batch is placed on that line's shifts for the standard's run time. */
	locationId?: number;
}

export const campaignsApi = {
	/**
	 * Get all campaigns for a company
	 */
	getByCompany: async (companyId: number): Promise<Campaign[]> => {
		const response = await axios.get<Campaign[]>(API_BASE_URL, {
			params: { companyId },
		});
		return response.data;
	},

	/**
	 * Create a new campaign
	 */
	create: async (campaign: SaveCampaignDto): Promise<Campaign> => {
		const response = await axios.post<Campaign>(API_BASE_URL, campaign);
		return response.data;
	},

	/**
	 * Update an existing campaign
	 */
	update: async (id: number, campaign: SaveCampaignDto): Promise<Campaign> => {
		const response = await axios.put<Campaign>(`${API_BASE_URL}/${id}`, campaign);
		return response.data;
	},

	/**
	 * Delete a campaign (the API returns 409 if orders were already generated)
	 */
	delete: async (id: number): Promise<void> => {
		await axios.delete(`${API_BASE_URL}/${id}`);
	},

	/**
	 * Get the work orders generated from a campaign, in sequence order
	 */
	getWorkOrders: async (id: number): Promise<CampaignWorkOrder[]> => {
		const response = await axios.get<CampaignWorkOrder[]>(`${API_BASE_URL}/${id}/work-orders`);
		return response.data;
	},

	/**
	 * Generate the next batch of sequenced work orders for a campaign.
	 * Repeat calls extend the sequence (e.g. #1-#5, then #6-#9).
	 */
	generate: async (id: number, request: GenerateCampaignOrdersDto): Promise<CampaignWorkOrder[]> => {
		const response = await axios.post<CampaignWorkOrder[]>(`${API_BASE_URL}/${id}/generate`, request);
		return response.data;
	},
};

export default campaignsApi;
